import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import type { ParsedCostLine, ParsedInvoice } from '@fleet/shared';
import { euro, fmtDate } from '@fleet/shared';
import { Badge, Button } from '../../design-system';
import { Eyebrow } from '../../components/ui/Eyebrow';

const cell: CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 13,
  padding: '6px 8px',
  border: '1px solid #C9BCA9',
  borderRadius: 4,
  background: '#FBF5EE',
  color: '#111111',
  width: '100%',
};
const mono: CSSProperties = { fontFamily: "'Roboto Mono', monospace" };
const th: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: '#666666',
};

/** One kenteken-blok of a deterministic MTC parse (read-only review). */
interface Groep {
  kenteken: string;
  regels: ParsedCostLine[];
  nieuw: number;
  duplicaten: number;
  bedrag: number;
  bedragNieuw: number;
  vanaf: string | null;
  tot: string | null;
}

function groepeer(regels: readonly ParsedCostLine[]): Groep[] {
  const byPlate = new Map<string, ParsedCostLine[]>();
  for (const r of regels) {
    const arr = byPlate.get(r.kenteken);
    if (arr) arr.push(r);
    else byPlate.set(r.kenteken, [r]);
  }
  return [...byPlate.entries()]
    .map(([kenteken, lijnen]) => {
      const datums = lijnen
        .map((l) => l.datum)
        .filter((d): d is string => !!d)
        .sort();
      const dup = lijnen.filter((l) => l.duplicaat).length;
      return {
        kenteken,
        regels: lijnen,
        nieuw: lijnen.length - dup,
        duplicaten: dup,
        bedrag: lijnen.reduce((s, l) => s + (l.bedragExcl || 0), 0),
        bedragNieuw: lijnen
          .filter((l) => !l.duplicaat)
          .reduce((s, l) => s + (l.bedragExcl || 0), 0),
        vanaf: datums[0] ?? null,
        tot: datums[datums.length - 1] ?? null,
      };
    })
    .sort((a, b) => b.bedrag - a.bedrag);
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <div
      style={{
        flex: '1 1 130px',
        padding: '10px 14px',
        background: '#FBF5EE',
        border: '1px solid #F0E8DC',
        borderRadius: 4,
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 900, color: accent ?? '#111111' }}>{value}</div>
      <div style={{ ...th, marginTop: 2 }}>{label}</div>
    </div>
  );
}

/** Grouped, read-only line review for a validated MTC transactieoverzicht. */
function GroepTabel({ groepen }: { groepen: Groep[] }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(new Set());
  const COLS = '24px 130px 1fr 90px 80px 100px 120px';

  function toggle(kenteken: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(kenteken)) next.delete(kenteken);
      else next.add(kenteken);
      return next;
    });
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLS,
          gap: 10,
          padding: '8px 0',
          borderBottom: '2px solid #111111',
          minWidth: 720,
          ...th,
        }}
      >
        <span />
        <span>Kenteken</span>
        <span>Periode</span>
        <span style={{ textAlign: 'right' }}>Regels</span>
        <span style={{ textAlign: 'right' }}>Nieuw</span>
        <span style={{ textAlign: 'right' }}>Al aanwezig</span>
        <span style={{ textAlign: 'right' }}>Excl. btw</span>
      </div>
      {groepen.map((g) => {
        const isOpen = open.has(g.kenteken);
        const dood = g.nieuw === 0;
        return (
          <div key={g.kenteken} style={{ borderBottom: '1px solid #F0E8DC' }}>
            <div
              onClick={() => toggle(g.kenteken)}
              style={{
                display: 'grid',
                gridTemplateColumns: COLS,
                gap: 10,
                padding: '9px 0',
                alignItems: 'center',
                fontSize: 13,
                cursor: 'pointer',
                minWidth: 720,
                opacity: dood ? 0.5 : 1,
              }}
            >
              <span style={{ color: '#8C8C82', fontSize: 11, textAlign: 'center' }}>
                {isOpen ? '▾' : '▸'}
              </span>
              <span style={{ ...mono, fontWeight: 700 }}>{g.kenteken}</span>
              <span style={{ color: '#666666' }}>
                {g.vanaf ? `${fmtDate(g.vanaf)} – ${fmtDate(g.tot)}` : '—'}
              </span>
              <span style={{ textAlign: 'right' }}>{g.regels.length}</span>
              <span style={{ textAlign: 'right', fontWeight: 700 }}>{g.nieuw}</span>
              <span style={{ textAlign: 'right', color: g.duplicaten ? '#B8801E' : '#B4B4A9' }}>
                {g.duplicaten || '—'}
              </span>
              <span style={{ textAlign: 'right', fontWeight: 700 }}>{euro(g.bedrag)}</span>
            </div>
            {isOpen && (
              <div
                style={{
                  maxHeight: 300,
                  overflowY: 'auto',
                  margin: '0 0 10px 34px',
                  border: '1px solid #F0E8DC',
                  borderRadius: 4,
                }}
              >
                {g.regels.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '90px 50px 1fr 1fr 90px 90px 90px',
                      gap: 8,
                      padding: '5px 10px',
                      fontSize: 12,
                      borderBottom: '1px solid #F7F2E9',
                      alignItems: 'center',
                      opacity: r.duplicaat ? 0.45 : 1,
                    }}
                  >
                    <span style={mono}>{r.datum ?? r.maand}</span>
                    <span style={{ color: '#666666' }}>{r.tijd ?? ''}</span>
                    <span>{r.product ?? r.omschrijving ?? r.categorie}</span>
                    <span style={{ color: '#666666' }}>
                      {[r.station, r.plaats].filter(Boolean).join(' · ')}
                    </span>
                    <span style={{ textAlign: 'right', color: '#666666' }}>
                      {r.hoeveelheid != null ? `${r.hoeveelheid} ${r.eenheid ?? ''}`.trim() : ''}
                    </span>
                    <span style={{ textAlign: 'right', fontWeight: 700 }}>
                      {euro(r.bedragExcl)}
                    </span>
                    <span style={{ textAlign: 'right' }}>
                      {r.duplicaat && <Badge tone="warning">dubbel</Badge>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Editable line review for AI- and CSV-results (few lines, may need fixes). */
function RegelTabel({
  regels,
  onRegel,
  onVerwijder,
}: {
  regels: readonly ParsedCostLine[];
  onRegel: (index: number, patch: Partial<ParsedCostLine>) => void;
  onVerwijder: (index: number) => void;
}) {
  const COLS = '110px 90px 130px 1fr 120px 80px 34px';
  return (
    <div style={{ overflowX: 'auto' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: COLS,
          gap: 10,
          padding: '8px 0',
          borderBottom: '2px solid #111111',
          minWidth: 700,
          ...th,
        }}
      >
        <span>Kenteken</span>
        <span>Maand</span>
        <span>Categorie</span>
        <span>Omschrijving</span>
        <span style={{ textAlign: 'right' }}>Excl. btw</span>
        <span>Status</span>
        <span />
      </div>
      {regels.map((r, i) => (
        <div
          key={i}
          style={{
            display: 'grid',
            gridTemplateColumns: COLS,
            gap: 10,
            padding: '7px 0',
            borderBottom: '1px solid #F0E8DC',
            alignItems: 'center',
            minWidth: 700,
            opacity: r.duplicaat ? 0.55 : 1,
          }}
        >
          <input
            style={{ ...cell, ...mono, fontWeight: 700 }}
            value={r.kenteken}
            onChange={(e) => onRegel(i, { kenteken: e.target.value.toUpperCase() })}
          />
          <input
            style={cell}
            value={r.maand}
            placeholder="JJJJ-MM"
            onChange={(e) => onRegel(i, { maand: e.target.value })}
          />
          <input
            style={cell}
            value={r.categorie}
            onChange={(e) => onRegel(i, { categorie: e.target.value })}
          />
          <input
            style={cell}
            value={r.omschrijving ?? ''}
            onChange={(e) => onRegel(i, { omschrijving: e.target.value || null })}
          />
          <input
            type="number"
            step="0.01"
            style={{ ...cell, textAlign: 'right', fontWeight: 700 }}
            value={r.bedragExcl}
            onChange={(e) => onRegel(i, { bedragExcl: Number(e.target.value) || 0 })}
          />
          <span>{r.duplicaat && <Badge tone="warning">dubbel</Badge>}</span>
          <button
            type="button"
            title="Regel verwijderen"
            onClick={() => onVerwijder(i)}
            style={{
              background: 'none',
              border: 'none',
              color: '#B0543B',
              fontSize: 18,
              cursor: 'pointer',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * Review step between parsing and saving. A validated MTC transactieoverzicht
 * (bron PARSER) is shown read-only and grouped per kenteken — the data is
 * checked against the document's own totals. AI- and CSV-results stay editable
 * per line. Exact duplicates are pre-flagged and skipped unless forced.
 */
export function PendingReview({
  pending,
  onChange,
  onConfirm,
  onDiscard,
  confirming,
}: {
  pending: ParsedInvoice;
  onChange: (p: ParsedInvoice) => void;
  onConfirm: (opts: { forceerDuplicaten: boolean }) => void;
  onDiscard: () => void;
  confirming: boolean;
}) {
  const [forceer, setForceer] = useState(false);

  const isParser = pending.bron === 'PARSER';
  const duplicaten = pending.regels.filter((r) => r.duplicaat).length;
  const nieuw = pending.regels.length - duplicaten;
  const importCount = forceer ? pending.regels.length : nieuw;
  const importBedrag = pending.regels
    .filter((r) => forceer || !r.duplicaat)
    .reduce((s, r) => s + (r.bedragExcl || 0), 0);

  const groepen = useMemo(
    () => (isParser ? groepeer(pending.regels) : []),
    [isParser, pending.regels],
  );

  const titel =
    pending.type === 'ONDERHOUD'
      ? 'Onderhoudsfactuur'
      : pending.bron === 'CSV'
        ? 'CSV-import'
        : 'Tankpasfactuur';

  function setHeader(patch: Partial<ParsedInvoice>) {
    onChange({ ...pending, ...patch });
  }
  function setRegel(i: number, patch: Partial<ParsedCostLine>) {
    onChange({
      ...pending,
      regels: pending.regels.map((r, idx) => (idx === i ? { ...r, ...patch } : r)),
    });
  }
  function removeRegel(i: number) {
    onChange({ ...pending, regels: pending.regels.filter((_, idx) => idx !== i) });
  }

  return (
    <div
      style={{ background: '#FFFFFF', border: '2px solid #111111', borderRadius: 4, padding: 24 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <Eyebrow color="#B0543B">Controleer de import</Eyebrow>
          <div style={{ fontSize: 18, fontWeight: 900, color: '#111111' }}>{titel}</div>
          <div style={{ fontSize: 13, color: '#666666' }}>
            {pending.bestandsnaam}
            {pending.periode ? <> · periode {pending.periode}</> : null}
            {pending.factuurnummer && isParser ? <> · ref. {pending.factuurnummer}</> : null}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
            {pending.bron === 'AI' && <Badge tone="blue">AI-analyse — controleer de regels</Badge>}
            {pending.bron === 'CSV' && <Badge tone="neutral">CSV-import</Badge>}
            {pending.controle && (
              <Badge tone="success">
                ✓ totalen kloppen met document ({euro(pending.controle.totaalExcl)})
              </Badge>
            )}
            {/* Wat er niet meekomt hoort naast het vinkje te staan, niet weggelaten:
                het bedrag hierboven is het documenttotaal, en dat is méér dan wat er
                straks in de database staat. */}
            {pending.controle?.overgeslagen && (
              <Badge tone="neutral">
                {pending.controle.overgeslagen.regels} {pending.controle.overgeslagen.wat} niet
                geïmporteerd ({euro(pending.controle.overgeslagen.bedrag)})
              </Badge>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button variant="ghost" onClick={onDiscard} disabled={confirming}>
            Annuleren
          </Button>
          <Button
            variant="primary"
            onClick={() => onConfirm({ forceerDuplicaten: forceer })}
            disabled={confirming || importCount === 0}
          >
            {confirming
              ? 'Importeren…'
              : `Importeer ${importCount} regel${importCount === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
        <Stat label="Regels in bestand" value={String(pending.regels.length)} />
        <Stat label="Nieuw" value={String(nieuw)} accent={nieuw > 0 ? '#2A6085' : '#8C8C82'} />
        <Stat
          label="Al aanwezig"
          value={String(duplicaten)}
          accent={duplicaten > 0 ? '#B8801E' : '#8C8C82'}
        />
        <Stat label="Te importeren bedrag" value={euro(importBedrag)} />
      </div>

      {duplicaten > 0 && (
        <div
          style={{
            marginTop: 12,
            padding: '12px 16px',
            background: '#F7E7C2',
            borderLeft: '3px solid #B8801E',
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ fontSize: 13, color: '#111111' }}>
            <strong>
              {duplicaten} regel{duplicaten === 1 ? ' staat' : 's staan'} al in het kostenoverzicht
            </strong>{' '}
            (bijvoorbeeld door een eerdere import van dezelfde periode).{' '}
            {forceer ? 'Ze worden nu tóch geïmporteerd.' : 'Ze worden overgeslagen.'}
          </span>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 13,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >
            <input
              type="checkbox"
              checked={forceer}
              onChange={(e) => setForceer(e.target.checked)}
            />
            Duplicaten toch importeren
          </label>
        </div>
      )}

      {!isParser && (
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16 }}>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              fontSize: 12,
              color: '#888888',
            }}
          >
            Factuurnummer
            <input
              style={{ ...cell, width: 160 }}
              value={pending.factuurnummer ?? ''}
              onChange={(e) => setHeader({ factuurnummer: e.target.value || null })}
            />
          </label>
          <label
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              fontSize: 12,
              color: '#888888',
            }}
          >
            Factuurdatum
            <input
              type="date"
              style={{ ...cell, width: 160 }}
              value={pending.datum ?? ''}
              onChange={(e) => setHeader({ datum: e.target.value || null })}
            />
          </label>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {isParser ? (
          <GroepTabel groepen={groepen} />
        ) : (
          <RegelTabel regels={pending.regels} onRegel={setRegel} onVerwijder={removeRegel} />
        )}
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 6,
          paddingTop: 12,
          fontSize: 14,
          fontWeight: 900,
        }}
      >
        Te importeren: {importCount} regel{importCount === 1 ? '' : 's'} · {euro(importBedrag)}
        {!forceer && duplicaten > 0 && (
          <span style={{ fontWeight: 400, color: '#8C8C82' }}>
            ({duplicaten} duplicaten overgeslagen)
          </span>
        )}
      </div>
    </div>
  );
}
